import React from 'react';
import { Form, Input, Button, Upload, Image, Card, Typography, Divider, Alert, Empty, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../services/responsiveUtils';
import TextEditor from '../TextEditor/TextEditor.component';
import DraggableList from '../DraggableList/DraggableList.component';
import './AdvertForm.styles.css';
import { AdvertEntity, AttachmentProps } from '../../../domain';

type TAdvertFormProps = {
  advert: AdvertEntity
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onUpload: (options: any) => void;
  isLoading: boolean;
  isSubmiting: boolean;
  onSubmit: () => void;
  buttonText: string;
  onRemoveFile: (attachment: AttachmentProps) => void;
  onReorderFiles: (newOrder: AttachmentProps[]) => void;
};

const AdvertForm: React.FC<TAdvertFormProps> = ({
  advert,
  onTitleChange,
  onDescriptionChange,
  onUpload,
  isLoading,
  isSubmiting,
  onSubmit,
  buttonText,
  onRemoveFile,
  onReorderFiles,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  const handleUpload = (options: any) => {
    const { file, onSuccess } = options;
    try {
      onUpload({ file, onSuccess });
    } catch (e: any) {
      const key = e?.message || 'upload-error';
      message.error(t(key, { ns: 'errors', limit: 10 }));
    }
  };

  const handlePreviewFile = (file: File | Blob) => {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
    });
  };

  return (
    <>
      {!isLoading && (
        <Form layout="vertical" onFinish={onSubmit} className="advert-form">
          <Card className="advert-form__card">
            <Form.Item label={t('advert-title', { ns: 'labels' })} required>
              <Input 
                placeholder={t('advert-title-placeholder', { ns: 'labels' })}
                value={advert.title?.Value ?? ''}
                onChange={(e) => onTitleChange(e.target.value)}
                disabled={isSubmiting}
                translate='no'
              />
            </Form.Item>
            
            <Form.Item label={t('advert-description', { ns: 'labels' })} required>
              {typeof advert.description === 'string' ? (
                <TextEditor 
                  labelText={t('advert-description', { ns: 'labels' })} 
                  value={advert.description} 
                  setValue={onDescriptionChange} 
                  disabled={isSubmiting} 
                />
              ) : null}
            </Form.Item>
            
            <Typography.Title level={5}>
              {t('advert-files', { ns: 'labels' })}
            </Typography.Title>
            
            <Alert
              message={t('advert-files-limit', { ns: 'labels' })}
              type="info"
              showIcon
              style={{ marginBottom: '15px' }}
            />
            
            <div className="advert-form__upload-container">
              <Image.PreviewGroup>
                <DraggableList 
                  items={advert.attachments.map(file => ({ ...file, id: file.uuid || `${file.url}-${file.position}` })) as Array<AttachmentProps & { id: string }>}
                  onReorder={(newOrder: Array<AttachmentProps & { id: string }>) => onReorderFiles(newOrder.map(({ id, ...rest }) => rest))}
                  renderItem={(file: AttachmentProps & { id: string }, index, isDragging) => (
                    <Card 
                      className="draggable-file-content" 
                      actions={[
                        <Button 
                          key="delete"
                          size="small" 
                          type="text" 
                          danger 
                          icon={<DeleteOutlined />} 
                          htmlType="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); const { id, ...rest } = file; onRemoveFile(rest as AttachmentProps); }}
                          aria-label="Delete"
                          title={t('delete', { ns: 'buttons' })}
                        />
                      ]}
                      title={`${index+1}`}
                    >
            {file.url ? (
                        <Image
              src={file.url}
                          alt={`File ${index + 1}`}
                          className="file-preview-image"
                          preview={{
                            maskClassName: "preview-mask",
                            visible: !isDragging
                          }}
                          onClick={(e) => isDragging && e.preventDefault()}
                          style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                        />
                      ) : (
                        <Empty 
                          image={Empty.PRESENTED_IMAGE_SIMPLE} 
                          description={
                            <Typography.Text className="file-name">
                              {`${t('file', { ns: 'labels' })} ${index + 1}`}
                            </Typography.Text>
                          }
                          style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                        />
                      )}
                    </Card>
                  )}
                  itemClassName="draggable-file-item"
                  className="draggable-file-list"
                  emptyMessage={t('no-files', { ns: 'labels' })}
                />
              </Image.PreviewGroup>
              
              <Divider>{t('upload-new-file', { ns: 'labels' })}</Divider>
              
              <Upload
                listType="picture-card"
                accept="image/*,video/*"
                multiple
                showUploadList={false}
                customRequest={handleUpload}
                disabled={isSubmiting}
              >
                <div
                  style={{
                    border: 0,
                    background: 'none',
                    padding: isMobile ? '5px' : '10px',
                    height: isMobile ? 70 : 100,
                    width: isMobile ? 70 : 100,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlusOutlined style={{ fontSize: '32px', color: '#ccc' }} />
                  <div style={{ marginTop: 8 }}>
                    {t('button-upload', { ns: 'buttons' })}
                  </div>
                </div>
              </Upload>
            </div>
            
            {buttonText && (
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={isSubmiting}
                style={{ marginTop: '20px' }}
              >
                {buttonText}
              </Button>
            )}
          </Card>
        </Form>
      )}
    </>
  );
};

export default AdvertForm;